import { DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, CapacitorCookies } from '@capacitor/core';
import { MANAGE_API_PATHS } from '../data';
import { UserDto } from '@marineform/data-access';
import { environment } from '@marineform/environment';
import {
  AccountSessionDto,
  ApiResponse,
  UserRecordDto,
} from '@splitsecondconnect/ultramarine-shared';
import { finalize, firstValueFrom, Observable, timeout } from 'rxjs';
import { manageLogoutInFlight } from '../interceptors/logout-gate.interceptor';

const NATIVE_AUTH_CALLBACK_SCHEME = 'racapp-manage';
const NATIVE_AUTH_CALLBACK_HOST = 'auth-callback';
const NATIVE_AUTH_LOADING_IN_KEY = 'manage|login|logging-in';
const NATIVE_AUTH_LOADING_OUT_KEY = 'manage|logout|logging-out';
const SIGN_OUT_TIMEOUT_MS = 5000;

/**
 * Manage sign-in and sign-out against session-authenticated services and `auth`.
 *
 * Logout posts to `/admin/auth/sign-out` first, aborting other
 * session-authenticated HTTP and `POST /session/refresh` so those calls
 * cannot persist a refreshed session cookie, then continues to `auth` logout.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #document = inject(DOCUMENT);
  #http = inject(HttpClient);
  #nativeAuthInitialized = signal(false);
  #processedAuthCodes = new Set<string>();
  #onNativeAuthExchanged: (() => Promise<void>) | null = null;
  #onNativeAuthLoading: {
    prepare?: () => Promise<void>;
    show: (messageKey?: string) => Promise<void>;
    dismiss: () => Promise<void>;
  } | null = null;

  /**
   * Registers loading UI shown while a warm native OAuth callback is processed.
   * Optional `prepare` runs when the auth browser opens so create work is done
   * before the callback; `show` then only needs to present.
   */
  onNativeAuthLoading(handlers: {
    prepare?: () => Promise<void>;
    show: (messageKey?: string) => Promise<void>;
    dismiss: () => Promise<void>;
  }): void {
    this.#onNativeAuthLoading = handlers;
  }

  /**
   * Registers a handler invoked after a warm native OAuth callback successfully
   * exchanges its code. Cold-start callbacks are handled by {@link initNativeAuth}
   * and the app initializer instead.
   */
  onNativeAuthExchanged(handler: () => Promise<void>): void {
    this.#onNativeAuthExchanged = handler;
  }

  /**
   * Registers native OAuth deep-link handling and replays a cold-start callback URL.
   * Must run before {@link login} so an immediate 401 can open the auth browser safely.
   */
  initNativeAuth(): Promise<void> {
    if (!Capacitor.isNativePlatform() || this.#nativeAuthInitialized()) {
      return Promise.resolve();
    }

    this.#nativeAuthInitialized.set(true);

    return (async () => {
      await App.addListener('appUrlOpen', (event) => {
        void (async () => {
          const exchanged = await this.#handleAuthCallbackUrl(event.url, {
            showLoading: true,
          });
          if (exchanged) {
            await this.#onNativeAuthExchanged?.();
          }
        })();
      });

      const launchUrl = await App.getLaunchUrl();
      if (launchUrl?.url) {
        await this.#handleAuthCallbackUrl(launchUrl.url);
      }
    })();
  }

  /**
   * Authenticate against POST `/admin/auth/sign-in`.
   *
   * The session cookie is already set by `auth`; this call returns the
   * `UserRecordDto` produced by `DynamicAuthGuard`.
   */
  login(): Observable<ApiResponse<UserRecordDto>> {
    return this.#http.post<ApiResponse<UserRecordDto>>(
      `${environment.ultramarineBaseUrl}/${MANAGE_API_PATHS.SIGNIN}`,
      {},
    );
  }

  /**
   * The sealed session's user. `organizationId` is null for an orgless renter.
   */
  getSessionUser(): Observable<UserDto> {
    return this.#http.get<UserDto>(`${environment.authnBaseUrl}/user`);
  }

  /**
   * Proto renter account for the sealed session.
   */
  getRenterSession(): Observable<ApiResponse<AccountSessionDto>> {
    return this.#http.post<ApiResponse<AccountSessionDto>>(
      `${environment.ultramarineBaseUrl}/${MANAGE_API_PATHS.PROTO_ACCOUNT_SESSION}`,
      {},
    );
  }

  /**
   * Send the browser to `auth` login, or open the native `auth` login sheet.
   */
  redirectToLogin() {
    if (Capacitor.isNativePlatform()) {
      this.#logInFromAuth();
      return;
    }

    this.#redirectBrowserToLogin();
  }

  /**
   * Open the portal home URL in the browser or a native sheet.
   */
  toPortal() {
    if (Capacitor.isNativePlatform()) {
      void Browser.open({
        url: environment.portalBaseUrl,
        presentationStyle: 'fullscreen',
      });
      return;
    }

    this.#document.location.href = environment.portalBaseUrl;
  }

  /**
   * End the manage session: abort in-flight session-authenticated HTTP and
   * refresh, POST sign-out, then destroy the session on `auth`.
   *
   * Sign-out errors and timeouts still continue to `auth` logout.
   * Wait is capped at 5 seconds.
   */
  logout() {
    manageLogoutInFlight.set(true);
    void this.#onNativeAuthLoading?.show(NATIVE_AUTH_LOADING_OUT_KEY);

    this.#http
      .post(`${environment.ultramarineBaseUrl}/${MANAGE_API_PATHS.SIGNOUT}`, {})
      .pipe(
        timeout(SIGN_OUT_TIMEOUT_MS),
        finalize(() => {
          manageLogoutInFlight.set(false);

          if (Capacitor.isNativePlatform()) {
            this.#logoutFromAuth();
            return;
          }

          this.#logoutFromAuthBrowser();
        }),
      )
      .subscribe({ error: () => undefined });
  }

  async #handleAuthCallbackUrl(
    urlString: string,
    options: { showLoading?: boolean } = {},
  ): Promise<boolean> {
    if (!this.#isNativeAuthCallbackUrl(urlString)) {
      return false;
    }

    let loadingShown = false;
    const dismissLoading = async () => {
      if (!loadingShown) {
        return;
      }
      loadingShown = false;
      await this.#onNativeAuthLoading?.dismiss();
    };

    // Present loading before closing the auth sheet so it is already on the
    // WebView when the sheet dismisses (awaiting Browser.close first left a gap).
    if (options.showLoading) {
      await this.#onNativeAuthLoading?.show(NATIVE_AUTH_LOADING_IN_KEY);
      loadingShown = true;
    }

    void Browser.close().catch(() => {
      // Browser may already be closed after a warm callback or launch-URL replay.
    });

    let url: URL;
    try {
      url = new URL(urlString);
    } catch {
      await dismissLoading();
      return false;
    }

    const code = url.searchParams.get('code');
    if (!code || this.#processedAuthCodes.has(code)) {
      await dismissLoading();
      return false;
    }

    try {
      await firstValueFrom(this.#exchangeCode(code));
      this.#processedAuthCodes.add(code);
      return true;
    } catch {
      // Exchange failures stay on the unauthenticated shell; no reload.
      await dismissLoading();
      return false;
    }
  }

  #isNativeAuthCallbackUrl(urlString: string): boolean {
    try {
      const url = new URL(urlString);
      return (
        url.protocol === `${NATIVE_AUTH_CALLBACK_SCHEME}:` &&
        url.hostname === NATIVE_AUTH_CALLBACK_HOST
      );
    } catch {
      return false;
    }
  }

  #exchangeCode(code: string): Observable<unknown> {
    return this.#http.post(
      `${environment.authnBaseUrl}/exchange`,
      { code },
      { withCredentials: true },
    );
  }

  #redirectBrowserToLogin() {
    this.#document.location.href = `${environment.authnBaseUrl}/login`;
  }

  #logoutFromAuthBrowser() {
    this.#document.location.href = `${environment.authnBaseUrl}/logout`;
  }

  #logInFromAuth() {
    void this.#onNativeAuthLoading?.prepare?.();
    void Browser.open({
      url: `${environment.authnBaseUrl}/login?isNative=true`,
      presentationStyle: 'popover',
    });
  }

  #logoutFromAuth() {
    void (async () => {
      this.#http
        .get(`${environment.authnBaseUrl}/logout`, {
          withCredentials: true,
        })
        .pipe(
          finalize(async () => {
            await CapacitorCookies.clearAllCookies();
            this.#document.defaultView?.location.reload();
          }),
        )
        .subscribe();
    })();
  }
}
